import { isNil, omitBy } from "lodash";

import type { WebsitePageSearchType } from "pro-features/components/AutocompleteWebsites/types";
import { getDomainWithSubs } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { getRecentParams } from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardUtils";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";

interface TransitionParams {
    keyValue: string;
    searchType?: WebsitePageSearchType;
    stateKey?: string;
    selectedPageTab?: string;
}

/**
 * Builds transition params for navigating to a keyword detail page.
 *
 * @param params an object containing the parameters for the transition
 * @param params.keyValue the value of the keyword to navigate to
 * @param params.searchType the search type of the keyword to navigate to, defaults to "exact"
 * @param params.stateKey the stateKey of the keyword detail page, defaults to "organicsearch_websites_v2_traffic_keywords"
 * @param params.selectedPageTab the selected page tab, defaults to "Paid"
 * @returns an object with the transition params
 */
export function getFinalTransitionParamsToKeywordDetailPage({
    keyValue,
    searchType = "exact",
    stateKey = "organicsearch_websites_v2_traffic_keywords",
    selectedPageTab = "Total",
}: TransitionParams): Record<string, string> {
    const defaultParams = proNavigator.getState(stateKey).defaultQueryParams;
    const recentParams = Object.fromEntries(Object.entries(getRecentParams() ?? {}).filter(([key]) => key !== "key"));

    return {
        ...defaultParams,
        ...recentParams,
        key: keyValue,
        selectedPageTab,
        pageFilter: JSON.stringify([{ url: keyValue, searchType }]),
    };
}

const WEB_KEYWORD_DETAIL_STATE = "organicsearch_websites_v2_traffic_keywords";

export function navigateToKeywordDetailPage({
    keyValue,
    searchType,
}: {
    keyValue: string;
    searchType: WebsitePageSearchType;
}): void {
    if (!proNavigator.isSalesSolution()) {
        void proNavigator.go(
            WEB_KEYWORD_DETAIL_STATE,
            getFinalTransitionParamsToKeywordDetailPage({ keyValue, searchType }),
        );
        return;
    }

    // Sales Intelligence maps the state to its own keywords page (SI ADR-0019). Keep the page's filters rather than
    // the WI recents, and keep `key` a plain domain: the SI header and siteInfo resolver can't handle folder paths.
    const { country, duration, webSource, isWWW } = proNavigator.getParams();
    void proNavigator.go(WEB_KEYWORD_DETAIL_STATE, {
        ...omitBy({ country, duration, webSource, isWWW }, isNil),
        key: getDomainWithSubs(keyValue),
        selectedPageTab: EKeywordTabs[EKeywordTabs.Total],
        pageFilter: JSON.stringify([{ url: keyValue, searchType }]),
    });
}
